import { Alert, Box, Button, TableRow, Typography } from "@mui/material";
import { OrderChangeRow, ProductOrder, SaleOrderEditEvent } from "../../../data/entities";
import CustomTable from "../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../shared/components/CustomTable/Styled";
import { formatVietnamTime } from "../../../utils/dateUtils";
import { OutsourcePanel, OutsourcePanelTitle } from "../../OutsourceOrder/components";
import { changeLabel, changeValue, historyRows } from "./orderEditHistory";

const ChangeRow = ({
    item,
    extraRowProps,
}: {
    item: OrderChangeRow;
    extraRowProps: { history: boolean };
}) => (
    <TableRow
        sx={{
            "& td": {
                verticalAlign: "top",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                fontFamily: '"Noto Sans", sans-serif',
                minWidth: extraRowProps.history ? 110 : 80,
            },
        }}
    >
        {extraRowProps.history && (
            <>
                <StyledTableCell>
                    {item.changed_at ? formatVietnamTime(item.changed_at, "DD/MM/YYYY HH:mm") : "—"}
                </StyledTableCell>
                <StyledTableCell>{item.editor}</StyledTableCell>
            </>
        )}
        <StyledTableCell>{changeLabel(item)}</StyledTableCell>
        <StyledTableCell>{changeValue(item.field, item.old_value)}</StyledTableCell>
        <StyledTableCell>{changeValue(item.field, item.new_value)}</StyledTableCell>
        {extraRowProps.history && (
            <StyledTableCell sx={{ minWidth: 200 }}>{item.reason || "—"}</StyledTableCell>
        )}
    </TableRow>
);

export const OrderChangesTable = ({
    rows,
    history = false,
}: {
    rows: OrderChangeRow[];
    history?: boolean;
}) => (
    <CustomTable
        itemList={rows}
        RowElement={ChangeRow}
        extraRowProps={{ history }}
        containerStyle={{
            overflowX: "auto",
            backgroundColor: "white",
            maxHeight: history ? 480 : 260,
            marginTop: 16,
            marginBottom: 16,
        }}
        TableColumns={(history
            ? ["Thời điểm", "Người sửa", "Nội dung", "Trước", "Sau", "Lý do"]
            : ["Nội dung", "Trước", "Sau"]
        ).map((text) => ({
            text,
            icon: null,
            width: history ? "110px" : undefined,
            sx: {
                minWidth: history ? 110 : 80,
                fontFamily: '"Noto Sans", sans-serif',
                backgroundColor: "white",
                fontWeight: 400,
            },
        }))}
    />
);

export const OrderHistory = ({ events = [] }: { events?: SaleOrderEditEvent[] }) => {
    const rows = historyRows(events);
    if (!rows.length) return null;
    return (
        <OutsourcePanel>
            <OutsourcePanelTitle>Lịch sử thay đổi</OutsourcePanelTitle>

            <OrderChangesTable rows={rows} history />
        </OutsourcePanel>
    );
};

export const CancellationBanner = ({
    order,
    retry,
    refreshing = false,
}: {
    order: Pick<ProductOrder, "cancel_reason" | "cancelled_at" | "cancelled_by">;
    retry?: () => void;
    refreshing?: boolean;
}) => (
    <Alert
        severity="error"
        icon={false}
        sx={{ mt: 2, mb: 2, backgroundColor: "#fff3f0", overflowWrap: "anywhere", borderRadius: 2 }}
        action={
            retry ? (
                <Button color="inherit" disabled={refreshing} onClick={retry}>
                    Tải lại
                </Button>
            ) : undefined
        }
    >
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#9f3324" }}>
            Đơn đã huỷ ·{" "}
            {order.cancelled_at ? formatVietnamTime(order.cancelled_at, "DD/MM/YYYY HH:mm") : "—"} ·{" "}
            {order.cancelled_by?.display_name || "Không rõ người sửa"}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 600, color: "#763d33" }}>
                Lý do huỷ:
            </Box>{" "}
            {order.cancel_reason || "—"}
        </Typography>
        <Typography variant="caption">
            Huỷ đơn chỉ đổi trạng thái, không xoá mất đơn hàng. Đơn này vẫn nằm trong danh sách,
            nhưng từ lúc huỷ thì không nhập kho và không xuất kho được nữa.
        </Typography>
    </Alert>
);
